/**
 * useResponseClipQueue
 *
 * PoC (WS Binary + Blob URL 配信) 用のクライアント側クリップキュー。
 * サーバーから WebSocket で送られてきた MP4 バイナリを順番に Blob URL 化し、
 * `<video>` の `src` として渡すための React フック。
 *
 * 設計の出典: zenryokukikai/clone-package useZenryokuLipSyncPlaybackQueue.ts
 * 詳細: docs/design/EXPERIMENT_20260612_VIDEO_DELIVERY_REDESIGN.md
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface QueuedClip {
  url: string;
  /** Web Audio で音声を鳴らすための元バイナリ（映像は muted 再生のため）。 */
  buffer: ArrayBuffer;
}

export interface UseResponseClipQueueResult {
  /** 現在再生対象の Blob URL（再生中でなければ null） */
  src: string | null;
  /** 現在再生対象クリップの音声デコード用バイナリ（Web Audio 用、なければ null） */
  audioBuffer: ArrayBuffer | null;
  /** 受信したバイナリをキューに追加し、必要なら再生を開始する */
  enqueueClip: (data: ArrayBuffer, mime?: string) => void;
  /** `<video>.onEnded` のコールバックとして呼び出す。次クリップへ進む */
  onClipEnded: () => void;
  /** キュー全体と再生中 URL を破棄。中断・セッション終了時に使用 */
  clearAll: () => void;
}

export function useResponseClipQueue(): UseResponseClipQueueResult {
  const [src, setSrc] = useState<string | null>(null);
  const [audioBuffer, setAudioBuffer] = useState<ArrayBuffer | null>(null);

  const currentUrlRef = useRef<string | null>(null);
  const queueRef = useRef<QueuedClip[]>([]);
  const playingRef = useRef<boolean>(false);

  const playNext = useCallback(() => {
    const next = queueRef.current.shift();
    if (!next) {
      playingRef.current = false;
      currentUrlRef.current = null;
      setSrc(null);
      setAudioBuffer(null);
      return;
    }
    playingRef.current = true;
    currentUrlRef.current = next.url;
    setSrc(next.url);
    setAudioBuffer(next.buffer);
  }, []);

  const enqueueClip = useCallback((data: ArrayBuffer, mime: string = 'video/mp4') => {
    try {
      const blob = new Blob([data], { type: mime });
      const url = URL.createObjectURL(blob);
      queueRef.current.push({ url, buffer: data });
      if (!playingRef.current) {
        playNext();
      }
    } catch (error) {
      console.error('[useResponseClipQueue] enqueueClip failed:', error);
    }
  }, [playNext]);

  const onClipEnded = useCallback(() => {
    if (currentUrlRef.current) {
      try {
        URL.revokeObjectURL(currentUrlRef.current);
      } catch (error) {
        console.warn('[useResponseClipQueue] revokeObjectURL failed:', error);
      }
      currentUrlRef.current = null;
    }
    playingRef.current = false;
    if (queueRef.current.length > 0) {
      playNext();
    } else {
      setSrc(null);
      setAudioBuffer(null);
    }
  }, [playNext]);

  const clearAll = useCallback(() => {
    if (currentUrlRef.current) {
      try {
        URL.revokeObjectURL(currentUrlRef.current);
      } catch {
        // ignore
      }
      currentUrlRef.current = null;
    }
    for (const item of queueRef.current) {
      try {
        URL.revokeObjectURL(item.url);
      } catch {
        // ignore
      }
    }
    queueRef.current = [];
    playingRef.current = false;
    setSrc(null);
    setAudioBuffer(null);
  }, []);

  useEffect(() => {
    return () => {
      clearAll();
    };
  }, [clearAll]);

  return { src, audioBuffer, enqueueClip, onClipEnded, clearAll };
}
